import { createFileRoute } from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { LegalPageLayout, LegalSectionHeading } from "@/components/legal-page-layout";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls, ogImageUrl } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
	component: Privacy,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/privacy",
		});

		return {
			meta: [
				{ title: "Privacy Policy - AgentFramer | MCP-Native Media Generation for AI Agents" },
				{
					name: "description",
					content:
						"Learn how AgentFramer protects your privacy. We explain what data we collect, how we use it, and your rights when using our platform.",
				},
				{ property: "og:title", content: "Privacy Policy - AgentFramer | MCP-Native Media Generation for AI Agents" },
				{
					property: "og:description",
					content: "How AgentFramer protects your privacy and handles your data.",
				},
				{
					property: "og:image",
					content: ogImageUrl("privacy"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("privacy") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/privacy",
			}),
		};
	},
});

const sidebarLinks = [
	{ id: "privacy-introduction", number: "01", label: "Introduction" },
	{ id: "privacy-collection", number: "02", label: "Data Collection" },
	{ id: "privacy-usage", number: "03", label: "Usage Schema" },
	{ id: "privacy-sharing", number: "04", label: "Model Providers" },
	{ id: "privacy-security", number: "05", label: "Security Standards" },
	{ id: "privacy-rights", number: "06", label: "Subject Rights" },
];

const modelBadges = ["OPENAI_GPT4", "ANTHROPIC_CLAUDE", "STABILITY_AI", "ELEVENLABS", "RUNWAY_ML"];

function Privacy() {
	return (
		<div style={{ background: "var(--km-surface)", color: "var(--km-on-surface)", minHeight: "100vh" }}>
			<PublicHeader />
			<LegalPageLayout
				contentsLabel="Protocol Contents"
				description="AgentFramer is built on the principle that trust is infrastructure. This document defines how we handle the data that flows through our MCP-native generation platform: what we collect, why we collect it, and the controls you hold as a data subject."
				lastUpdated="2026.03.01.UTC"
				sections={sidebarLinks}
				title={
					<>
						PRIVACY
						<br />
						<span style={{ color: "#5A33D4" }}>PROTOCOL.</span>
					</>
				}
				version="Policy v2.4.0"
			>
					<section id="privacy-introduction" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="01" title="System Overview" />
						<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
							AgentFramer operates as an MCP-native proxy layer between AI agents and
							generation providers. Data trust is foundational to this architecture. When your
							agent dispatches a tool call, we act as a stateful intermediary. We handle
							authentication, route the job, and return the asset URL. We do not train models
							on your generations or prompts.
						</p>
					</section>

					{/* 02 Data Collection */}
					<section id="privacy-collection" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="02" title="Data We Collect" />
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<DataCard
								title="Account Information"
								fields={["EMAIL_ID", "LEGAL_NAME", "BILLING_ADDRESS"]}
								description="Collected at registration and used for authentication, billing, and communication."
							/>
							<DataCard
								title="Usage Metadata"
								fields={["MCP_TOOL_CALL_LOGS", "GENERATION_HISTORY", "CREDIT_LEDGER"]}
								description="Telemetry from every agent interaction, used for service reliability and credit accounting."
							/>
						</div>
						<div
							className="p-5 flex items-start gap-4"
							style={{
								background: "var(--km-surface-low)",
								border: "1px solid rgba(255,255,255,0.08)",
							}}
						>
							<span
								aria-hidden="true"
								className="material-symbols-outlined mt-0.5"
								style={{ color: "#6B3FF2", fontSize: "20px" }}
							>
								lock
							</span>
							<div>
								<div
									className="text-sm font-bold mb-1"
									style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Media Outputs
								</div>
								<div className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
									Generated images, video clips, and audio files are stored in encrypted
									object storage. Asset URLs are signed and time-limited. We do not
									republish or redistribute your generated content.
								</div>
							</div>
						</div>
					</section>

					{/* 03 Usage Schema */}
					<section id="privacy-usage" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="03" title="How We Use Data" />
						<div className="space-y-2">
							{[
								{
									n: "#01",
									label: "Service Optimization",
									text: "We use generation metadata to improve job routing, model selection defaults, and latency across the MCP bridge.",
								},
								{
									n: "#02",
									label: "Credit Management",
									text: "Usage logs are the source of truth for your credit ledger. Every tool call is accounted for transparently.",
								},
								{
									n: "#03",
									label: "Fraud Synthesis",
									text: "Anomaly detection on usage patterns helps us protect all accounts from abuse and unauthorized access.",
								},
							].map((row) => (
								<div
									key={row.n}
									className="flex items-start gap-4 px-4 py-3"
									style={{
										background: "var(--km-surface-low)",
										border: "1px solid rgba(255,255,255,0.06)",
									}}
								>
									<span
										className="text-xs font-bold mt-0.5 shrink-0"
										style={{
											fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
											color: "#6B3FF2",
										}}
									>
										{row.n}
									</span>
									<div>
										<div
											className="text-sm font-bold mb-0.5"
											style={{ color: "var(--km-on-surface)" }}
										>
											{row.label}
										</div>
										<div className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
											{row.text}
										</div>
									</div>
								</div>
							))}
						</div>
					</section>

					{/* 04 Model Providers */}
					<section id="privacy-sharing" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="04" title="Data Sharing" />
						<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
							To fulfil generation requests, we transmit prompt data and configuration to
							third-party model providers. We work with trusted service providers bound by
							data processing agreements. Legal disclosures may occur when required by valid
							legal process.
						</p>
						<div className="flex flex-wrap gap-2">
							{modelBadges.map((badge) => (
								<span
									key={badge}
									className="text-xs px-3 py-1.5"
									style={{
										fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
										background: "#201f1f",
										border: "1px solid rgba(255,255,255,0.08)",
										color: "var(--km-on-surface-muted)",
									}}
								>
									{badge}
								</span>
							))}
						</div>
						<div
							className="px-4 py-3 text-sm font-bold flex items-center gap-2"
							style={{
								background: "rgba(234,40,4,0.04)",
								border: "1px solid #6B3FF2",
								color: "#6B3FF2",
							}}
						>
							<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "16px" }}>
								verified
							</span>
							We do not sell your personal information to any third party.
						</div>
					</section>

					{/* 05 Security */}
					<section id="privacy-security" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="05" title="Security Standards" />
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<div
								className="p-5 space-y-2"
								style={{
									background: "var(--km-surface-low)",
									border: "1px solid rgba(255,255,255,0.08)",
								}}
							>
								<div className="flex items-center gap-2">
									<span
										aria-hidden="true"
										className="material-symbols-outlined"
										style={{ color: "#6B3FF2", fontSize: "18px" }}
									>
										shield_lock
									</span>
									<span
										className="text-sm font-bold"
										style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
									>
										Transit Encryption
									</span>
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
									All data in transit is encrypted using TLS 1.3. MCP connections use
									mutual TLS where supported.
								</p>
							</div>
							<div
								className="p-5 space-y-2"
								style={{
									background: "var(--km-surface-low)",
									border: "1px solid rgba(255,255,255,0.08)",
								}}
							>
								<div className="flex items-center gap-2">
									<span
										aria-hidden="true"
										className="material-symbols-outlined"
										style={{ color: "#6B3FF2", fontSize: "18px" }}
									>
										encrypted
									</span>
									<span
										className="text-sm font-bold"
										style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
									>
										Rest Encryption
									</span>
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
									Stored assets and credentials are encrypted at rest using AES-256.
									Access is audited and role-gated.
								</p>
							</div>
						</div>
						<div className="flex flex-wrap gap-2">
							{["SOC2_TYPE_II", "AES_256_REST", "TLS_1.3_TRANSIT", "RBAC_ACCESS"].map((badge) => (
								<span
									key={badge}
									className="text-xs px-2.5 py-1"
									style={{
										fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
										background: "rgba(234,40,4,0.05)",
										border: "1px solid rgba(234,40,4,0.12)",
										color: "#6B3FF2",
									}}
								>
									{badge}
								</span>
							))}
						</div>
					</section>

					{/* 06 Subject Rights + Contact */}
					<section id="privacy-rights" className="space-y-5 scroll-mt-28">
						<LegalSectionHeading number="06" title="Subject Rights + Contact" />
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<div
								className="p-5 space-y-3"
								style={{
									background: "var(--km-surface-low)",
									border: "1px solid rgba(255,255,255,0.08)",
								}}
							>
								<div
									className="text-sm font-bold"
									style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Subject Rights
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
									You hold data sovereignty over your account. You may access, correct,
									export, or delete your data at any time. Requests are fulfilled within
									30 days.
								</p>
								<ul className="space-y-1">
									{[
										"Access and download your data",
										"Correct inaccurate information",
										"Delete your account and data",
										"Opt out of marketing communications",
										"Export generated assets and history",
										"Request data processing information",
									].map((right) => (
										<li
											key={right}
											className="text-xs flex items-start gap-2"
											style={{ color: "var(--km-on-surface-muted)" }}
										>
											<span
												aria-hidden="true"
												className="material-symbols-outlined mt-0.5"
												style={{ fontSize: "12px", color: "#6B3FF2" }}
											>
												check_circle
											</span>
											{right}
										</li>
									))}
								</ul>
								<a
									href="https://agentframer.com/help"
									className="inline-flex items-center gap-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{ color: "#6B3FF2", textDecoration: "underline dotted #6B3FF2" }}
								>
									Help Center
									<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "12px" }}>
										arrow_forward
									</span>
								</a>
							</div>
							<div
								className="p-5 space-y-3"
								style={{
									background: "var(--km-surface-low)",
									border: "1px solid rgba(255,255,255,0.08)",
								}}
							>
								<div
									className="text-sm font-bold"
									style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Contact
								</div>
								<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
									Privacy questions, data requests, or regulatory inquiries can be directed
									to our legal team.
								</p>
								<a
									href="mailto:legal@agentframer.com"
									aria-label="Send email to legal@agentframer.com"
									className="block px-3 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{
										fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
										background: "rgba(234,40,4,0.05)",
										border: "1px solid rgba(234,40,4,0.12)",
										borderLeft: "2px solid #6B3FF2",
										color: "#6B3FF2",
										textDecoration: "underline dotted #6B3FF2",
									}}
								>
									legal@agentframer.com
								</a>
							</div>
						</div>
					</section>
			</LegalPageLayout>

			{/* Footer */}
			<PublicFooter />
		</div>
	);
}

function DataCard({
	title,
	fields,
	description,
}: {
	title: string;
	fields: string[];
	description: string;
}) {
	return (
		<div
			className="p-5 space-y-3"
			style={{
				background: "var(--km-surface-low)",
				border: "1px solid rgba(255,255,255,0.08)",
			}}
		>
			<div
				className="text-sm font-bold"
				style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
			>
				{title}
			</div>
			<div className="flex flex-wrap gap-1.5">
				{fields.map((field) => (
					<span
						key={field}
						className="text-xs px-2 py-0.5"
						style={{
							fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
							background: "rgba(234,40,4,0.05)",
							border: "1px solid rgba(234,40,4,0.08)",
							color: "#6B3FF2",
						}}
					>
						{field}
					</span>
				))}
			</div>
			<p className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
				{description}
			</p>
		</div>
	);
}
